/**
 * Helpers para conversão de timezone entre BRT (Brasil) e UTC
 * 
 * PREMISSA ARQUITETURAL:
 * - Backend armazena TUDO em UTC (timestamp with time zone)
 * - Frontend trabalha em horário LOCAL do usuário (BRT = UTC-3)
 * - Conversão acontece na BORDA (entrada/saída do backend)
 */

/**
 * Offset do Brasil em relação ao UTC (em horas)
 * BRT = UTC-3 (não consideramos horário de verão)
 */
const BRT_OFFSET_HOURS = 3;

/**
 * Converte horário local do Brasil (BRT) para UTC ISO string
 * 
 * @param {string} date - Data no formato YYYY-MM-DD (ex: "2025-11-27")
 * @param {string} time - Horário no formato HH:mm (ex: "08:00")
 * @returns {string} ISO string em UTC (ex: "2025-11-27T11:00:00.000Z")
 * 
 * @example
 * // Usuário quer agendar para 27/11/2025 às 08:00 (horário de Brasília)
 * const utc = convertBRTtoUTC("2025-11-27", "08:00");
 * // Resultado: "2025-11-27T11:00:00.000Z" (11:00 UTC = 08:00 BRT)
 */
export function convertBRTtoUTC(date, time) {
  // Construir DateTime local no formato ISO
  const localDateTime = new Date(`${date}T${time}:00`);
  
  // Converter para UTC adicionando o offset
  const utcDateTime = new Date(
    localDateTime.getTime() + (BRT_OFFSET_HOURS * 60 * 60 * 1000)
  );
  
  return utcDateTime.toISOString();
}

/**
 * Converte ISO string UTC para componentes de data/hora local BRT
 * 
 * @param {string} isoString - ISO string UTC (ex: "2025-11-27T11:00:00Z")
 * @returns {{date: string, time: string}} Objeto com date (YYYY-MM-DD) e time (HH:mm)
 * 
 * @example
 * // Backend retorna "2025-11-27T11:00:00Z" (11:00 UTC)
 * const { date, time } = convertUTCtoBRT("2025-11-27T11:00:00Z");
 * // Resultado: { date: "2025-11-27", time: "08:00" } (08:00 BRT)
 */
export function convertUTCtoBRT(isoString) {
  const utcDate = new Date(isoString);
  
  // Converter para horário local subtraindo o offset
  const localDate = new Date(utcDate.getTime() - (BRT_OFFSET_HOURS * 60 * 60 * 1000));
  
  const date = localDate.toISOString().split('T')[0];
  const time = localDate.toISOString().split('T')[1].substring(0, 5);
  
  return { date, time };
}

/**
 * Converte date + time local para Date object em BRT
 * Útil para comparações e manipulações antes de enviar ao backend
 * 
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:mm
 * @returns {Date} Date object representando o horário local
 */
export function createLocalDateTime(date, time) {
  return new Date(`${date}T${time}:00`);
}

/**
 * Formata Date UTC para exibição em horário local BRT
 * 
 * @param {Date|string} dateOrString - Date object ou ISO string
 * @param {object} options - Opções de formatação do toLocaleTimeString
 * @returns {string} Horário formatado em BRT
 * 
 * @example
 * formatToBRT("2025-11-27T11:00:00Z");
 * // Resultado: "08:00"
 */
export function formatToBRT(dateOrString, options = { hour: '2-digit', minute: '2-digit' }) {
  const date = typeof dateOrString === 'string' ? new Date(dateOrString) : dateOrString;
  return date.toLocaleTimeString('pt-BR', {
    ...options,
    timeZone: 'America/Sao_Paulo'
  });
}
